import i18n from "../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShoppingCart, Users, Receipt, CircleDollarSign, TrendingUp, Printer, Plus, Minus, Trash2, ScanText, Package, PawPrint, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { farmDateIso } from "@/lib/farmTime";

const PAYMENT_METHODS = ["cash", "bank", "gcash", "maya", "cheque", "mixed"];
const CUSTOMER_OPTIONS = [{ id: "__walkin__", name: "Walk-in" }];
const PRODUCT_CATEGORIES = ["all", "meat", "fish", "vegetables", "fruits"];

function MetricCard({ label, value, sub, icon: Icon, tone = "default" }) {
    const tones = { default: "", good: "bg-primary/5 border-primary/20", warn: "bg-amber-500/5 border-amber-500/20", bad: "bg-destructive/5 border-destructive/20" };
    return (
        <Card className={`p-4 ${tones[tone]}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <div className="tiny-label truncate">{label}</div>
                    <div className="mt-2 text-2xl font-bold font-mono truncate">{value}</div>
                    {sub ? <div className="mt-1 text-xs text-muted-foreground truncate">{sub}</div> : null}
                </div>
                {Icon ? <Icon className="w-5 h-5 text-primary shrink-0" /> : null}
            </div>
        </Card>
    );
}

function EmptyState({ title, description }) {
    return <Card className="p-6 text-center text-sm text-muted-foreground"><div className="font-semibold text-foreground">{title}</div><div className="mt-1">{description}</div></Card>;
}

function toNumber(value) {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
}

function uid(prefix) {
    return `${prefix}-${farmDateIso().replace(/-/g, "")}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
}

function buildReceiptDoc(farm, receipt) {
    if (!receipt) return null;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.setFont(undefined, "bold");
    doc.text(farm.name || "AJ's Farm", 14, 16);
    doc.setFontSize(10);
    doc.setFont(undefined, "normal");
    doc.text("Sales Receipt", 14, 22);
    doc.text(receipt.invoice_number || receipt.id, 14, 28);
    doc.text(receipt.customer_name || "Walk-in", 14, 34);
    doc.text((receipt.created_at || "").slice(0, 10), 14, 40);
    autoTable(doc, {
        startY: 48,
        head: [["Item", "Qty", "Unit Price", "Subtotal"]],
        body: (receipt.items || []).map((item) => [item.name, `${item.quantity} ${item.unit || ""}`.trim(), formatCurrency(item.unit_price || 0, farm.currency), formatCurrency(item.subtotal || 0, farm.currency)]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [64, 145, 108] },
    });
    const y = doc.lastAutoTable.finalY + 8;
    doc.text(`Subtotal: ${formatCurrency(receipt.subtotal || 0, farm.currency)}`, 14, y);
    doc.text(`Tax: ${formatCurrency(receipt.tax || 0, farm.currency)}`, 14, y + 6);
    doc.text(`Discount: ${formatCurrency(receipt.discount || 0, farm.currency)}`, 14, y + 12);
    doc.setFont(undefined, "bold");
    doc.text(`Total: ${formatCurrency(receipt.grand_total || receipt.total || 0, farm.currency)}`, 14, y + 20);
    return doc;
}

export default function SalesPage() {
    const { can, isOwner } = useAuth();
    const canSell = can("add");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [farm, setFarm] = useState({ currency: "PHP" });
    const [summary, setSummary] = useState(null);
    const [products, setProducts] = useState([]);
    const [animals, setAnimals] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [cart, setCart] = useState([]);
    const [receipt, setReceipt] = useState(null);
    const [tab, setTab] = useState("sales");
    const [q, setQ] = useState("");
    const [customerId, setCustomerId] = useState("__walkin__");
    const [customerName, setCustomerName] = useState("");
    const [tax, setTax] = useState(0);
    const [discount, setDiscount] = useState(0);
    const [payMethod, setPayMethod] = useState("cash");
    const [payAmount, setPayAmount] = useState("");
    const [reference, setReference] = useState("");
    const [processing, setProcessing] = useState(false);
    const [animalId, setAnimalId] = useState("");
    const [animalPrice, setAnimalPrice] = useState("");
    const [previewInvoice, setPreviewInvoice] = useState(null);

    const load = async () => {
        setLoading(true);
        setError("");
        try {
            const [farmResp, summaryResp, productsResp, animalsResp, customersResp, invoicesResp] = await Promise.all([
                api.get("/farm").then((r) => r.data).catch(() => ({ currency: "PHP" })),
                api.get("/sales-dashboard").then((r) => r.data).catch(() => null),
                api.get("/products").then((r) => r.data).catch(() => []),
                api.get("/animals").then((r) => r.data).catch(() => []),
                api.get("/customers/with-balance").then((r) => r.data).catch(() => []),
                api.get("/invoices").then((r) => r.data).catch(() => []),
            ]);
            setFarm(farmResp || { currency: "PHP" });
            setSummary(summaryResp);
            setProducts(Array.isArray(productsResp) ? productsResp : []);
            setAnimals(Array.isArray(animalsResp) ? animalsResp : []);
            setCustomers(Array.isArray(customersResp) ? customersResp : []);
            setInvoices(Array.isArray(invoicesResp) ? invoicesResp : []);
        } catch (err) {
            setError(err.response?.data?.detail || err.message || "Failed to load sales data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const cur = (value) => formatCurrency(value || 0, farm.currency || "PHP");

    const filteredProducts = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return products.filter((product) => {
            if (PRODUCT_CATEGORIES[0] !== "all" && false) return true;
            return [product.name, product.sku, product.category, product.subcategory].join(" ").toLowerCase().includes(needle);
        });
    }, [products, q]);

    const filteredAnimals = useMemo(() => {
        const needle = q.trim().toLowerCase();
        return animals.filter((animal) => [animal.tag_id, animal.name, animal.species_name, animal.status].join(" ").toLowerCase().includes(needle));
    }, [animals, q]);

    const total = useMemo(() => cart.reduce((sum, item) => sum + toNumber(item.subtotal), 0), [cart]);
    const grandTotal = Math.max(total + toNumber(tax) - toNumber(discount), 0);
    const outstandingInvoices = useMemo(() => invoices.filter((invoice) => ["unpaid", "sent", "partial", "overdue"].includes(String(invoice.status || invoice.payment_status || "").toLowerCase())), [invoices]);
    const paidInvoices = useMemo(() => invoices.filter((invoice) => ["paid", "partial"].includes(String(invoice.status || invoice.payment_status || "").toLowerCase())), [invoices]);
    const animalSales = useMemo(() => invoices.filter((invoice) => (invoice.items || []).some((item) => item.animal_id)), [invoices]);
    const productSales = useMemo(() => invoices.filter((invoice) => (invoice.items || []).some((item) => item.product_id)), [invoices]);

    const addProduct = (product) => {
        setCart((prev) => {
            const existing = prev.find((item) => item.product_id === product.id);
            if (existing) {
                const nextQty = existing.quantity + 1;
                if (nextQty > toNumber(product.current_stock)) {
                    toast.error(`Only ${product.current_stock} in stock`);
                    return prev;
                }
                return prev.map((item) => item.product_id === product.id ? { ...item, quantity: nextQty, subtotal: nextQty * item.unit_price } : item);
            }
            if (toNumber(product.current_stock) <= 0) {
                toast.error("Out of stock");
                return prev;
            }
            return [...prev, {
                product_id: product.id,
                name: product.name,
                sku: product.sku,
                unit: product.unit,
                unit_price: toNumber(product.unit_price),
                quantity: 1,
                subtotal: toNumber(product.unit_price),
                stock: toNumber(product.current_stock),
                kind: "product",
            }];
        });
    };

    const addAnimal = () => {
        const animal = animals.find((entry) => entry.id === animalId);
        if (!animal) return toast.error("Select an animal first");
        const price = toNumber(animalPrice);
        if (price <= 0) return toast.error("Enter a valid sale price");
        setCart((prev) => {
            if (prev.some((item) => item.animal_id === animal.id)) {
                toast.error("Animal already in cart");
                return prev;
            }
            return [...prev, {
                animal_id: animal.id,
                name: animal.tag_id || animal.name || animal.id,
                sku: animal.tag_id || animal.id,
                unit: "head",
                unit_price: price,
                quantity: 1,
                subtotal: price,
                stock: 1,
                kind: "animal",
            }];
        });
        setAnimalId("");
        setAnimalPrice("");
    };

    const changeQty = (id, delta) => {
        setCart((prev) => prev.map((item) => {
            if ((item.product_id || item.animal_id) !== id) return item;
            const nextQty = item.quantity + delta;
            if (nextQty <= 0) return null;
            if (nextQty > item.stock) {
                toast.error(`Only ${item.stock} available`);
                return item;
            }
            return { ...item, quantity: nextQty, subtotal: nextQty * item.unit_price };
        }).filter(Boolean));
    };

    const removeItem = (id) => setCart((prev) => prev.filter((item) => (item.product_id || item.animal_id) !== id));
    const clearCart = () => {
        setCart([]);
        setTax(0);
        setDiscount(0);
        setPayAmount("");
        setReference("");
        setCustomerId("__walkin__");
        setCustomerName("");
        setAnimalId("");
        setAnimalPrice("");
    };

    const sale = async (paidNow) => {
        if (!canSell) return toast.error("You do not have permission to create sales");
        if (cart.length === 0) return toast.error("Cart is empty");
        setProcessing(true);
        try {
            const body = {
                customer_id: customerId && customerId !== "__walkin__" ? customerId : null,
                customer_name: customerId && customerId !== "__walkin__" ? (customers.find((entry) => entry.id === customerId)?.name || customerName || "Walk-in") : (customerName || "Walk-in"),
                items: cart.map((item) => ({
                    product_id: item.product_id,
                    animal_id: item.animal_id,
                    name: item.name,
                    quantity: item.quantity,
                    unit_price: item.unit_price,
                })),
                tax: toNumber(tax),
                discount: toNumber(discount),
                notes: "Sales module checkout",
                source_submission_id: uid("sale"),
            };
            if (paidNow) {
                body.payment = {
                    amount: toNumber(payAmount) || grandTotal,
                    method: payMethod,
                    reference,
                };
            }
            const result = await api.post("/pos/sell", body);
            setReceipt(result.data);
            setPreviewInvoice(result.data);
            toast.success(paidNow ? "Sale complete" : "Invoice created");
            clearCart();
            load();
            setTab("history");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to create sale");
        } finally {
            setProcessing(false);
        }
    };

    const payInvoice = async (invoice, amount) => {
        try {
            await api.post(`/invoices/${invoice.id}/pay`, { amount: toNumber(amount) || toNumber(invoice.grand_total || invoice.total), method: payMethod, reference: reference || uid("pay") });
            toast.success("Payment recorded");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Payment failed");
        }
    };

    const cancelInvoice = async (invoice) => {
        if (!isOwner()) return toast.error("Owner permission required");
        try {
            await api.post(`/invoices/${invoice.id}/cancel`, { reason: "Cancelled from Sales module" });
            toast.success("Invoice cancelled");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Cancel failed");
        }
    };

    const refundInvoice = async (invoice) => {
        if (!isOwner()) return toast.error("Owner permission required");
        try {
            await api.post(`/invoices/${invoice.id}/refund`, { reason: "Refunded from Sales module" });
            toast.success("Invoice refunded");
            load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Refund failed");
        }
    };

    const printInvoice = (invoice) => {
        const doc = buildReceiptDoc(farm, invoice);
        if (doc) doc.save(`${invoice.invoice_number || invoice.id}.pdf`);
    };

    if (loading) return <div className="p-8 text-center text-muted-foreground">{i18n.t("batch3.98635e8c6343")}</div>;
    if (error) {
        return <Card className="p-6 border-destructive/40 bg-destructive/5" data-testid="sales-error"><div className="font-semibold text-destructive">{i18n.t("batch3.734a57d75218")}</div><div className="text-sm text-muted-foreground mt-1">{error}</div><Button className="mt-4" variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch3.9f5cd8a2e880")}</Button></Card>;
    }

    const dashboard = summary?.data || summary || {};
    const kpis = dashboard.kpis || {};
    const topProducts = dashboard.top_products || [];
    const topCustomers = dashboard.top_customers || [];
    const trend = dashboard.trend || [];

    return (
        <div className="space-y-6" data-testid="sales-page">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <div className="tiny-label">{i18n.t("batch3.ae65324b857d")}</div>
                    <h1 className="text-4xl font-bold tracking-tight mt-2">{i18n.t("batch3.d0edfb6e8c55")}</h1>
                    <p className="text-muted-foreground mt-2">{i18n.t("batch3.85bfb2b6f8f1")}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" />{i18n.t("batch3.56e3badc4e6c")}</Button>
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="sales-kpis">
                <MetricCard label={i18n.t("batch3.24345a14377f")} value={cur(kpis.today_sales || 0)} icon={TrendingUp} />
                <MetricCard label={i18n.t("batch3.082bc378cd60")} value={cur(kpis.month_sales || 0)} icon={CircleDollarSign} />
                <MetricCard label={i18n.t("batch3.35f8f37b92ae")} value={formatNumber(kpis.month_invoices || invoices.length)} icon={Receipt} />
                <MetricCard label={i18n.t("batch3.50cc12f69e8f")} value={formatNumber(kpis.unpaid_invoices || outstandingInvoices.length)} icon={ScanText} tone={outstandingInvoices.length > 0 ? "warn" : "default"} />
                <MetricCard label={i18n.t("batch3.035eae60290f")} value={formatNumber(customers.length)} icon={Users} />
                <MetricCard label={i18n.t("batch3.9150bcc33369")} value={formatNumber(animalSales.length)} icon={PawPrint} />
                <MetricCard label={i18n.t("batch3.14fbc6d9a834")} value={formatNumber(productSales.length)} icon={Package} />
                <MetricCard label={i18n.t("batch3.dc9d4584a554")} value={formatNumber(paidInvoices.length)} icon={ShoppingCart} />
            </div>

            <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="h-auto flex flex-wrap gap-1" data-testid="sales-tabs">
                    <TabsTrigger value="sales">{i18n.t("batch3.8870388c7105")}</TabsTrigger>
                    <TabsTrigger value="history">{i18n.t("batch3.cae905d7914f")}</TabsTrigger>
                    <TabsTrigger value="customers">{i18n.t("batch3.e6d551b7005d")}</TabsTrigger>
                    <TabsTrigger value="payments">{i18n.t("batch3.659dba210997")}</TabsTrigger>
                    <TabsTrigger value="analytics">{i18n.t("batch3.25bc96295797")}</TabsTrigger>
                </TabsList>

                <TabsContent value="sales" className="space-y-4">
                    <div className="grid gap-4 lg:grid-cols-[1.3fr_0.7fr]">
                        <Card className="min-w-0 p-4 space-y-4">
                            <div className="grid gap-3 md:grid-cols-3">
                                <div><Label>{i18n.t("batch3.0e85749a6f40")}</Label>
                                    <Select value={customerId} onValueChange={(value) => setCustomerId(value)}>
                                        <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={i18n.t("batch3.6817176b8305")} /></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="__walkin__">{i18n.t("batch3.801d866beb88")}</SelectItem>
                                            {customers.map((customer) => <SelectItem key={customer.id} value={customer.id}>{customer.name} {customer.outstanding ? `· ${cur(customer.outstanding)}` : ""}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div><Label>{i18n.t("batch3.6a2c211d3147")}</Label><Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("batch3.0c6c4102d4df")} /></div>
                                <div><Label>{i18n.t("batch3.5f612f65fc3f")}</Label><Input value={q} onChange={(e) => setQ(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("batch3.cba7e62fc003")} /></div>
                            </div>

                            <div className="grid gap-4 xl:grid-cols-2">
                                <Card className="min-w-0 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="font-semibold">{i18n.t("batch3.14fbc6d9a834")}</div>
                                        <Badge variant="outline">{i18n.t("batch3.baca91664986")}</Badge>
                                    </div>
                                    {filteredProducts.length === 0 ? <EmptyState title={i18n.t("batch3.6a98e192a983")} description={i18n.t("batch3.d558761bed9b")} /> : (
                                        <div className="space-y-2 max-h-[420px] overflow-auto pr-1">
                                            {filteredProducts.slice(0, 24).map((product) => (
                                                <div key={product.id} className="rounded-lg border p-3 flex items-center justify-between gap-3">
                                                    <div className="min-w-0">
                                                        <div className="font-semibold truncate">{product.name}</div>
                                                        <div className="text-xs text-muted-foreground">{product.sku || "—"} · {product.category || "product"} {i18n.t("batch3.e2234e0631a8")} {formatNumber(product.current_stock || 0)}</div>
                                                    </div>
                                                    <div className="flex items-center gap-2 shrink-0">
                                                        <div className="text-right text-sm">
                                                            <div className="font-semibold">{cur(product.unit_price || 0)}</div>
                                                            <div className="text-xs text-muted-foreground">{product.unit || "unit"}</div>
                                                        </div>
                                                        <Button size="sm" onClick={() => addProduct(product)} disabled={!canSell || toNumber(product.current_stock) <= 0}><Plus className="w-4 h-4" /></Button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>

                                <Card className="min-w-0 p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="font-semibold">{i18n.t("batch3.9150bcc33369")}</div>
                                        <Badge variant="outline">{i18n.t("batch3.9387477796af")}</Badge>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2 mb-3">
                                        <div><Label>{i18n.t("batch3.7389b3b752fa")}</Label>
                                            <Select value={animalId} onValueChange={setAnimalId}>
                                                <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={i18n.t("batch3.179ac1a75869")} /></SelectTrigger>
                                                <SelectContent>
                                                    {filteredAnimals.slice(0, 60).map((animal) => <SelectItem key={animal.id} value={animal.id}>{animal.tag_id || animal.name || animal.id} {animal.status ? `· ${animal.status}` : ""}</SelectItem>)}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div><Label>{i18n.t("batch3.4552eaf4d115")}</Label><Input type="number" min="0" step="0.01" value={animalPrice} onChange={(e) => setAnimalPrice(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("batch3.86b6e9c44c38")} /></div>
                                        <Button onClick={addAnimal} disabled={!canSell} className="h-11"><PawPrint className="w-4 h-4 mr-2" />{i18n.t("batch3.c677df573f74")}</Button>
                                    </div>
                                    <div className="text-xs text-muted-foreground mb-2">{i18n.t("batch3.28484e9d2642")}</div>
                                    <div className="space-y-2 max-h-[280px] overflow-auto pr-1">
                                        {filteredAnimals.slice(0, 18).map((animal) => (
                                            <div key={animal.id} className="rounded-lg border p-3 flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="font-semibold truncate">{animal.tag_id || animal.name || animal.id}</div>
                                                    <div className="text-xs text-muted-foreground truncate">{animal.species_name || animal.species || "Animal"} · {animal.status || "unknown"}</div>
                                                </div>
                                                <Button size="sm" variant="outline" onClick={() => { setAnimalId(animal.id); setAnimalPrice(animal.current_value || animal.estimated_value || 0); }} disabled={!canSell}>{i18n.t("batch3.8598222918d3")}</Button>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </div>
                        </Card>

                        <Card className="min-w-0 p-4 space-y-4">
                            <div className="font-semibold">{i18n.t("batch3.44656820495b")}</div>
                            {cart.length === 0 ? <EmptyState title={i18n.t("batch3.f07ca04bdec7")} description={i18n.t("batch3.c8e4fecc6486")} /> : (
                                <div className="space-y-2 max-h-[360px] overflow-auto pr-1">
                                    {cart.map((item) => (
                                        <div key={item.product_id || item.animal_id} className="rounded-lg border p-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="font-semibold truncate">{item.name}</div>
                                                    <div className="text-xs text-muted-foreground">{item.sku || "—"} · {item.kind}</div>
                                                </div>
                                                <Button size="sm" variant="ghost" onClick={() => removeItem(item.product_id || item.animal_id)}><Trash2 className="w-4 h-4" /></Button>
                                            </div>
                                            <div className="mt-3 flex items-center justify-between gap-2 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => changeQty(item.product_id || item.animal_id, -1)}><Minus className="w-3.5 h-3.5" /></Button>
                                                    <span className="min-w-10 text-center font-mono">{item.quantity}</span>
                                                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => changeQty(item.product_id || item.animal_id, 1)}><Plus className="w-3.5 h-3.5" /></Button>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-semibold">{cur(item.subtotal)}</div>
                                                    <div className="text-xs text-muted-foreground">{cur(item.unit_price)} {i18n.t("batch3.b32f279e548b")}</div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <div className="grid gap-3 md:grid-cols-2">
                                <div><Label>{i18n.t("batch3.9be70f66f8dd")}</Label><Input type="number" min="0" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} className="h-11 mt-1" /></div>
                                <div><Label>{i18n.t("batch3.b524936d7aa1")}</Label><Input type="number" min="0" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-11 mt-1" /></div>
                                <div><Label>{i18n.t("batch3.f383f6a23c87")}</Label>
                                    <Select value={payMethod} onValueChange={setPayMethod}>
                                        <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                                        <SelectContent>{PAYMENT_METHODS.map((method) => <SelectItem key={method} value={method}>{method}</SelectItem>)}</SelectContent>
                                    </Select>
                                </div>
                                <div><Label>{i18n.t("batch3.db1c784524e1")}</Label><Input value={reference} onChange={(e) => setReference(e.target.value)} className="h-11 mt-1" placeholder={i18n.t("batch3.0c6c4102d4df")} /></div>
                                <div><Label>{i18n.t("batch3.748c323563fa")}</Label><Input type="number" min="0" step="0.01" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="h-11 mt-1" placeholder={formatNumber(grandTotal)} /></div>
                            </div>

                            <div className="rounded-lg border p-3 space-y-1 text-sm">
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.97f7359ed801")}</span><span className="font-semibold">{cur(total)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.9be70f66f8dd")}</span><span className="font-semibold">{cur(tax)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.b524936d7aa1")}</span><span className="font-semibold">{cur(discount)}</span></div>
                                <div className="flex items-center justify-between pt-2 border-t"><span className="font-semibold">{i18n.t("batch3.650fe991ac42")}</span><span className="font-bold text-lg">{cur(grandTotal)}</span></div>
                            </div>

                            <div className="grid grid-cols-2 gap-2">
                                <Button variant="outline" onClick={clearCart} disabled={!cart.length}>{i18n.t("batch3.719ea396ad92")}</Button>
                                <Button onClick={() => sale(false)} disabled={!canSell || processing || cart.length === 0}>{i18n.t("batch3.32815b0bb690")}</Button>
                                <Button className="col-span-2" onClick={() => sale(true)} disabled={!canSell || processing || cart.length === 0}>{i18n.t("batch3.4532461123e1")}</Button>
                            </div>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="history" className="space-y-4">
                    <Card className="p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                            <div>
                                <div className="font-semibold">{i18n.t("batch3.cae905d7914f")}</div>
                                <div className="text-sm text-muted-foreground">{i18n.t("batch3.19adf6127aec")}</div>
                            </div>
                        </div>
                        {invoices.length === 0 ? <EmptyState title={i18n.t("batch3.560512c253ff")} description={i18n.t("batch3.286daeb2a843")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch3.f9f38818c406")}</TableHead><TableHead>{i18n.t("batch3.0e85749a6f40")}</TableHead><TableHead>{i18n.t("batch3.bae7d5be7082")}</TableHead><TableHead>{i18n.t("batch3.44d25b5d1b6d")}</TableHead><TableHead className="text-right">{i18n.t("batch3.b25928c69902")}</TableHead><TableHead className="text-right">{i18n.t("batch3.dc9d4584a554")}</TableHead><TableHead /></TableRow></TableHeader>
                                <TableBody>
                                    {invoices.slice(0, 60).map((invoice) => (
                                        <TableRow key={invoice.id}>
                                            <TableCell className="font-semibold">{invoice.invoice_number || invoice.id}</TableCell>
                                            <TableCell>{invoice.customer_name || "Walk-in"}</TableCell>
                                            <TableCell><Badge variant="outline">{invoice.status || invoice.payment_status || "unpaid"}</Badge></TableCell>
                                            <TableCell>{formatNumber((invoice.items || []).length)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(invoice.grand_total || invoice.total)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(invoice.total_paid || 0)}</TableCell>
                                            <TableCell className="text-right space-x-2">
                                                <Button size="sm" variant="outline" onClick={() => setPreviewInvoice(invoice)}>{i18n.t("batch3.f1fbb2b43dca")}</Button>
                                                <Button size="sm" variant="outline" onClick={() => printInvoice(invoice)}><Printer className="w-3.5 h-3.5 mr-1" />{i18n.t("batch3.5b221e9c2a45")}</Button>
                                                <Button size="sm" variant="outline" onClick={() => payInvoice(invoice, invoice.grand_total || invoice.total)}>{i18n.t("batch3.80c37573afed")}</Button>
                                                {isOwner() ? <Button size="sm" variant="outline" onClick={() => cancelInvoice(invoice)}>{i18n.t("batch3.77dfd2135f4d")}</Button> : null}
                                                {isOwner() ? <Button size="sm" variant="outline" onClick={() => refundInvoice(invoice)}>{i18n.t("batch3.e17c8ad0dc2e")}</Button> : null}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="customers" className="space-y-4">
                    <Card className="p-4">
                        <div className="font-semibold mb-3">{i18n.t("batch3.e6d551b7005d")}</div>
                        {customers.length === 0 ? <EmptyState title={i18n.t("batch3.b692be478a94")} description={i18n.t("batch3.f1a20bd22739")} /> : (
                            <Table>
                                <TableHeader><TableRow><TableHead>{i18n.t("batch3.709a23220f2c")}</TableHead><TableHead className="text-right">{i18n.t("batch3.35f8f37b92ae")}</TableHead><TableHead className="text-right">{i18n.t("batch3.f8ee57ec8645")}</TableHead><TableHead className="text-right">{i18n.t("batch3.c2f1302a0a50")}</TableHead><TableHead className="text-right">{i18n.t("batch3.7c62a1424469")}</TableHead></TableRow></TableHeader>
                                <TableBody>
                                    {customers.map((customer) => (
                                        <TableRow key={customer.id}>
                                            <TableCell className="font-semibold">{customer.name}</TableCell>
                                            <TableCell className="text-right font-mono">{formatNumber(customer.invoice_count || 0)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(customer.outstanding || 0)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(customer.credit_limit || 0)}</TableCell>
                                            <TableCell className="text-right font-mono">{cur(customer.credit_available || 0)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </Card>
                </TabsContent>

                <TabsContent value="payments" className="space-y-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch3.659dba210997")}</div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.700edaf90808")}</span><span className="font-semibold">{formatNumber(outstandingInvoices.length)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.474108de344b")}</span><span className="font-semibold">{formatNumber(paidInvoices.length)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.f67c40299562")}</span><span className="font-semibold">{formatNumber(animalSales.length)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.31b956ddaa0e")}</span><span className="font-semibold">{formatNumber(productSales.length)}</span></div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch3.7a20bf140d9c")}</div>
                            <div className="text-sm text-muted-foreground">{i18n.t("batch3.09f52b85861f")}</div>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="analytics" className="space-y-4">
                    <div className="grid gap-4 lg:grid-cols-2">
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch3.b93abe4c560c")}</div>
                            <div className="space-y-3 text-sm">
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.a25ca5d39a61")}</span><span className="font-semibold">{cur(kpis.today_sales || 0)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.949bac1aa62e")}</span><span className="font-semibold">{cur(kpis.month_sales || 0)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.f292b8081d8b")}</span><span className="font-semibold">{cur(kpis.aov || 0)}</span></div>
                                <div className="flex items-center justify-between"><span className="text-muted-foreground">{i18n.t("batch3.b6c600062323")}</span><span className="font-semibold">{formatNumber(topCustomers.length)}</span></div>
                            </div>
                        </Card>
                        <Card className="p-4">
                            <div className="font-semibold mb-3">{i18n.t("batch3.21808a71ead8")}</div>
                            <div className="space-y-2 text-sm">
                                {topProducts.slice(0, 5).map((row) => (
                                    <div key={row.name} className="flex items-center justify-between gap-3">
                                        <span className="truncate">{row.name}</span>
                                        <span className="font-semibold">{cur(row.revenue || 0)}</span>
                                    </div>
                                ))}
                                {trend.slice(0, 5).map((row) => (
                                    <div key={row.date} className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                                        <span>{row.date}</span>
                                        <span>{cur(row.revenue || 0)} · {formatNumber(row.invoices || 0)} {i18n.t("batch3.c3e815afba60")}</span>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </div>
                </TabsContent>
            </Tabs>

            <Dialog open={!!previewInvoice} onOpenChange={(open) => !open && setPreviewInvoice(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader><DialogTitle>{i18n.t("batch3.a00475b4d631")}</DialogTitle></DialogHeader>
                    {previewInvoice ? (
                        <div className="space-y-4">
                            <div className="rounded-lg border p-4">
                                <div className="font-semibold">{previewInvoice.invoice_number || previewInvoice.id}</div>
                                <div className="text-sm text-muted-foreground">{previewInvoice.customer_name || "Walk-in"}</div>
                                <div className="mt-3 space-y-1 text-sm">
                                    {(previewInvoice.items || []).map((item) => (
                                        <div key={`${item.name}-${item.quantity}`} className="flex items-center justify-between gap-3">
                                            <span>{item.name}</span><span>{item.quantity} {i18n.t("batch3.11f6ad8ec52a")} {cur(item.unit_price)}</span>
                                        </div>
                                    ))}
                                </div>
                                <div className="mt-3 border-t pt-3 flex items-center justify-between font-semibold"><span>{i18n.t("batch3.b25928c69902")}</span><span>{cur(previewInvoice.grand_total || previewInvoice.total)}</span></div>
                            </div>
                            <div className="flex gap-2">
                                <Button variant="outline" className="flex-1" onClick={() => printInvoice(previewInvoice)}><Printer className="w-4 h-4 mr-2" />{i18n.t("batch3.4e896ec2eec4")}</Button>
                                <Button variant="outline" className="flex-1" onClick={() => setPreviewInvoice(null)}>{i18n.t("batch3.bbfa773e5a63")}</Button>
                            </div>
                        </div>
                    ) : null}
                </DialogContent>
            </Dialog>
        </div>
    );
}
